<script setup lang="ts">
import { ref, shallowRef, computed, watch } from 'vue'
import { choose, closeChoose } from './components/filters/bricks'
import { openInformation } from './components/information'
import { useCanvas } from '.'
import { api, clientSession } from '@/services/api'

import Bricks from '@/container/components/filters/bricks/Bricks.vue'
import Bonds from '@/container/components/filters/Bonds.vue'
import Sizes from '@/container/components/filters/Sizes.vue'
import Joints from '@/container/components/filters/Joints.vue'

import Actions from '@/container/components/Actions.vue'

import Accordion, { type AccordionObject } from '@/components/Accordion.vue'
import Popover from '@/components/Popover.vue'
import Options from '@/container/components/Options.vue'

import type { AppType, CanvasOptions } from '=/types'

const props = defineProps<{ app: AppType }>()

const imgElement = ref<HTMLCanvasElement | null>(null)

const initial = await api.get<CanvasOptions>(`/api/initial`)

const { values, rules, render } = await useCanvas(imgElement, initial, props.app)

watch(values.form, render, { deep: true, immediate: true })

const info = props.app.lang.bricks.tooltip

const accordions = shallowRef<AccordionObject[]>([
  {
    title: props.app.lang.bonds.title,
    info: props.app.lang.bonds.tooltip,
    component: Bonds
  },
  {
    title: props.app.lang.sizes.title,
    info: props.app.lang.sizes.tooltip,
    component: Sizes
  },
  {
    title: props.app.lang.joints.title,
    info: props.app.lang.joints.tooltip,
    component: Joints,
    hasHr: false
  }
])

const logo = computed(() => `${import.meta.env.VITE_BACKEND_URL}assets/${props.app.class}/${props.app.logo}`)
</script>

<template>
  <canvas ref="imgElement" class="content__img"></canvas>
  <div class="config" :class="{ more: choose }">
    <div class="config__header">
      <img :src="logo" alt="Logo" />
    </div>
    <div class="config__main" v-outside="closeChoose">
      <div class="config__content" :class="{ visible: choose }">
        <h2 class="config__title">
          {{ app.lang.bricks.title }}
          <svg class="info" v-floating:click="[Popover, { info }]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15">
            <path fill="#A8A29E" d="M7.5 14.25a6.75 6.75 0 1 0 0-13.5 6.75 6.75 0 0 0 0 13.5Z" />
            <path fill="#A8A29E" d="M7.5 0a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15Zm0 1.5a6 6 0 1 0 0 12 6 6 0 0 0 0-12Z" />
            <path fill="#FFF" d="M8.482 9.375a.75.75 0 0 1 0 1.5H6.75a.75.75 0 1 1 0-1.5h1.732Z" />
            <path fill="#FFF" d="M7.62 6.188a.75.75 0 0 1 .75.75v3.187a.75.75 0 1 1-1.5 0l-.001-2.438h-.112a.75.75 0 0 1-.743-.648l-.007-.101a.75.75 0 0 1 .75-.75h.862Z" />
            <path fill="#FFF" d="M7.388 3.748c.241 0 .456.114.593.292l.02.032.032.02a.75.75 0 0 1 .285.492l.007.102a.937.937 0 1 1-.937-.938Z" />
            <path fill="#FFF" d="M7.388 3.748c.517 0 .937.42.937.938a.75.75 0 0 1-1.344.458l-.02-.032-.032-.02a.75.75 0 0 1-.285-.492l-.006-.102a.75.75 0 0 1 .75-.75Z" />
          </svg>
          <span v-if="choose && !clientSession?.first" @click.prevent="closeChoose">
            <svg class="icon-close-main" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="#292524" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 12h14" />
            </svg>
          </span>
        </h2>
        <p class="config__text">{{ app.lang.bricks.description }}</p>
        <Bricks v-model:values="values" v-model:show="choose" :rules />
        <Accordion v-model:accordion="accordions" v-model:values="values" :rules :app />
      </div>
    </div>
  </div>
  <Options />
  <Actions @render="render" @export="openInformation" :app />
</template>
